import { businessDate } from "./stock";

const cents = value => Math.round(Number(value || 0) * 100);
const same = (a, b) => String(a) === String(b);
const scoped = (id, showroom) => !showroom || same(id, showroom);

// An invoice records sales value; its receipt records money movement separately.
export function journalAmounts(row) {
  return {
    sales: row.type === "sale" ? row.amount : row.type === "return" ? -row.amount : null,
    money: ["receipt","income"].includes(row.type) ? row.amount : row.type === "expense" ? -row.amount : null,
  };
}

// Print one invoice row with the receipts collected for it on this journal day.
// Payments on older invoices, or received at another showroom, remain separate.
export function journalPrintRows(rows) {
  const key = row => JSON.stringify([
    String(row.saleId ?? (row.type === "sale" ? row.reference : row.invoice)),
    String(row.showroomId ?? row.showroom), journalDate(row.date),
  ]);
  const sales = new Map(rows.filter(row => row.type === "sale").map(row => [key(row), { receipts: [], total: 0 }]));
  const merged = new Set();
  for (const row of rows) {
    if (row.type !== "receipt") continue;
    const sale = sales.get(key(row));
    if (!sale) continue;
    sale.receipts.push(row);
    sale.total += cents(row.amount);
    merged.add(row);
  }
  return rows.filter(row => !merged.has(row)).map(row => {
    if (row.type !== "sale") return row;
    const sale = sales.get(key(row));
    return { ...row, receipts: sale.receipts, receivedAmount: sale.receipts.length ? sale.total / 100 : null };
  });
}

export function journalDate(value) {
  const text = String(value || "");
  return /(?:Z|[+-]\d{2}:?\d{2})$/i.test(text)
    ? businessDate(new Date(text)) : text.slice(0, 10);
}

// Find the actual Cairo day boundary, including the 23/25-hour DST days.
export function journalDayBounds(date) {
  const anchor = Date.parse(`${date}T00:00:00Z`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !Number.isFinite(anchor) ||
      new Date(anchor).toISOString().slice(0, 10) !== date) throw new Error("Select a valid date.");
  const midnight = day => {
    const target = Date.parse(`${day}T00:00:00Z`);
    let lo = target - 18 * 3600000, hi = target + 18 * 3600000;
    while (lo < hi) {
      const mid = Math.floor((lo + hi) / 2);
      if (businessDate(new Date(mid)) < day) lo = mid + 1;
      else hi = mid;
    }
    return new Date(lo).toISOString();
  };
  const next = new Date(anchor + 86400000).toISOString().slice(0, 10);
  return { start: midnight(date), end: midnight(next) };
}

export function buildDailyJournal({ sales = [], lines = [], payments = [], returns = [],
  returnLines = [], manualIncome = [], expenses = [], customers = [], showrooms = [], blocks = [] }, date, showroom = "") {
  const customerName = id => customers.find(c => same(c.id, id))?.name || "—";
  const showroomName = id => showrooms.find(s => same(s.id, id))?.name || "—";
  const saleMap = new Map(sales.map(s => [String(s.id), s]));
  const rows = [];
  for (const sale of sales) {
    if (journalDate(sale.date) !== date || !scoped(sale.showroom_id, showroom)) continue;
    const items = lines.filter(line => same(line.sale_id, sale.id)).map(line => ({ ...line,
      code: blocks.find(b => same(b.id, line.block_id))?.code || `#${line.block_id}` }));
    const gross = cents(sale.gross_total ?? (items.length
      ? items.reduce((sum, line) => sum + Number(line.total_price || 0), 0)
      : sale.total_amount ?? sale.total_price ?? 0));
    const discount = sale.discount_amount != null ? cents(sale.discount_amount) :
      sale.discount_type === "percent" ? Math.round(gross * Number(sale.discount_value || 0) / 100) : cents(sale.discount_value);
    const amount = sale.net_total != null ? cents(sale.net_total) : Math.max(0, gross - discount);
    rows.push({ key: `sale-${sale.id}`, type: "sale", saleId: sale.id, showroomId: sale.showroom_id, reference: `AH-${sale.id}`, date: sale.date,
      customer: customerName(sale.customer_id), showroom: showroomName(sale.showroom_id), amount: amount / 100,
      description: "Sales invoice", gross: gross / 100, discount: discount / 100, items });
  }
  for (const note of returns) {
    if (journalDate(note.date) !== date) continue;
    const original = saleMap.get(String(note.original_sale_id));
    const items = returnLines.filter(line => same(line.return_id, note.id));
    const matching = items.filter(line => scoped(line.showroom_id ?? original?.showroom_id, showroom));
    if (showroom && (items.length ? !matching.length : !scoped(original?.showroom_id, showroom))) continue;
    const amount = showroom && items.length ? matching.reduce((sum, line) => sum + cents(line.line_refund_amount), 0) :
      note.total_refund != null ? cents(note.total_refund) : matching.reduce((sum, line) => sum + cents(line.line_refund_amount), 0);
    const locations = [...new Set(matching.map(line => line.showroom_id ?? original?.showroom_id))];
    rows.push({ key: `return-${note.id}`, type: "return", reference: `Return #${note.id}`, date: note.date,
      customer: customerName(note.customer_id), showroom: !showroom && locations.length > 1 ? "Multiple showrooms" : showroomName(showroom || locations[0] || original?.showroom_id),
      amount: amount / 100, description: note.reason || "Sales return", invoice: `AH-${note.original_sale_id}` });
  }
  for (const payment of payments) {
    if (journalDate(payment.payment_date) !== date) continue;
    const sale = saleMap.get(String(payment.sale_id));
    const location = payment.showroom_id ?? sale?.showroom_id;
    if (!scoped(location, showroom)) continue;
    rows.push({ key: `payment-${payment.id}`, type: "receipt", saleId: payment.sale_id, showroomId: location, reference: payment.voucher_no || `Receipt #${payment.id}`,
      date: payment.payment_date, customer: customerName(payment.customer_id ?? sale?.customer_id),
      showroom: showroomName(location), amount: cents(payment.amount) / 100, method: payment.method || "cash",
      description: payment.notes || "Customer payment", invoice: payment.sale_id ? `AH-${payment.sale_id}` : "Customer account",
      paymentReference: payment.reference });
  }
  for (const [type, entries] of [["income", manualIncome], ["expense", expenses]]) {
    for (const entry of entries) {
      if (journalDate(entry.date) !== date || !scoped(entry.showroom_id, showroom)) continue;
      rows.push({ key: `${type}-${entry.id}`, id: entry.id, type, reference: entry.voucher_no || `#${entry.id}`,
        date: entry.date, customer: "—", showroom: showroomName(entry.showroom_id), amount: cents(entry.amount) / 100,
        method: entry.method || "cash", description: entry.description, costCenter: entry.cost_center, manual: true });
    }
  }
  rows.sort((a, b) => String(a.date).localeCompare(String(b.date)) || a.key.localeCompare(b.key, "en", { numeric: true }));
  const sum = type => rows.filter(row => row.type === type).reduce((total, row) => total + cents(row.amount), 0);
  const salesTotal = sum("sale"), returnsTotal = sum("return"), receipts = sum("receipt"), income = sum("income"), expense = sum("expense");
  return { rows, totals: { sales: salesTotal / 100, returns: returnsTotal / 100, netSales: (salesTotal - returnsTotal) / 100,
    receipts: receipts / 100, income: income / 100, expenses: expense / 100,
    netReceipts: (receipts + income - expense) / 100, salesLessExpenses: (salesTotal - returnsTotal - expense) / 100 } };
}

async function readRows(client, table, columns, filter = q => q) {
  const rows = [];
  for (let offset = 0; ; offset += 1000) {
    const { data, error } = await filter(client.from(table).select(columns).order("id").range(offset, offset + 999));
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < 1000) return rows;
  }
}

async function relatedRows(client, table, column, ids, columns = "*") {
  const unique = [...new Set(ids.filter(id => id != null))], rows = [];
  for (let offset = 0; offset < unique.length; offset += 200) {
    rows.push(...await readRows(client, table, columns, q => q.in(column, unique.slice(offset, offset + 200))));
  }
  return rows;
}

export async function loadDailyJournal(client, date, showroom = "") {
  const bounds = journalDayBounds(date);
  const byShowroom = q => showroom ? q.eq("showroom_id", showroom) : q;
  const timestampDay = q => q.gte("date", bounds.start).lt("date", bounds.end);
  const [daySales, payments, returns, manualIncome, expenses, showrooms] = await Promise.all([
    readRows(client, "sale", "*", q => byShowroom(q.eq("date", date))),
    readRows(client, "payment", "*", q => q.eq("payment_date", date)),
    readRows(client, "return_note", "*", timestampDay),
    readRows(client, "daily_income_manual", "*", q => byShowroom(timestampDay(q))),
    readRows(client, "daily_expense", "*", q => byShowroom(timestampDay(q))),
    readRows(client, "showroom", "id,name", q => showroom ? q.eq("id", showroom) : q),
  ]);
  const missingIds = [...payments.map(p => p.sale_id), ...returns.map(r => r.original_sale_id)]
    .filter(id => id != null && !daySales.some(s => same(s.id, id)));
  const [relatedSales, lines, returnLines] = await Promise.all([
    relatedRows(client, "sale", "id", missingIds),
    relatedRows(client, "sale_details", "sale_id", daySales.map(s => s.id)),
    relatedRows(client, "return_note_details", "return_id", returns.map(r => r.id)),
  ]);
  const sales = [...daySales, ...relatedSales];
  const [customers, blocks] = await Promise.all([
    relatedRows(client, "customer", "id", [...sales.map(s => s.customer_id), ...payments.map(p => p.customer_id), ...returns.map(r => r.customer_id)], "id,name"),
    relatedRows(client, "block", "id", lines.map(line => line.block_id), "id,code"),
  ]);
  return { ...buildDailyJournal({ sales, lines, payments, returns, returnLines, manualIncome, expenses, customers, showrooms, blocks }, date, showroom), showrooms };
}
